import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import { ThemeContext } from '../../context/ThemeContext';
import { API_BASE } from '../../api';

function AllEditorTasks() {
  const [tasks, setTasks] = useState([]);
  const [designers, setDesigners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showAdminNotesModal, setShowAdminNotesModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [deletingTask, setDeletingTask] = useState(null);
  const { currentTheme } = useContext(ThemeContext);
  const navigate = useNavigate();


  // Colors
  const colors = {
    backgroundDark: '#0f0c29',
    backgroundMedium: '#302b63',
    backgroundLight: '#24243e',
    textLight: 'rgba(255, 255, 255, 0.8)',
    borderDark: 'rgba(255, 255, 255, 0.2)',
    primary: currentTheme?.primary || '#6366F1',
    secondary: currentTheme?.secondary || '#8B5CF6',
    accent: currentTheme?.accent || '#06B6D4',
    success: '#10B981',
    warning: '#F59E0B',
    danger: '#EF4444',
    info: '#3B82F6'
  };

  useEffect(() => {
    fetchEditorTasks();
    fetchDesigners();
  }, []);

  const fetchEditorTasks = async () => {
    try {
      setLoading(true);
      setMessage('');
      
      const response = await fetch(`${API_BASE}/get_all_editor_tasks.php`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success && Array.isArray(data.tasks)) {
        console.log('Fetched tasks with editor data:', data.tasks);
        setTasks(data.tasks);
      } else {
        setTasks([]);
        setMessage('No tasks found');
      }
    } catch (error) {
      console.error('Error fetching editor tasks:', error);
      setMessage(`❌ Error: ${error.message}`);
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchDesigners = async () => {
    try {
      const response = await fetch(`${API_BASE}/get_designers.php`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success && Array.isArray(data.data)) {
        setDesigners(data.data);
      } else {
        setDesigners([]);
      }
    } catch (error) {
      console.error('Error fetching designers:', error);
      setDesigners([]);
    }
  };

  const handleEditClick = (taskId) => {
    navigate(`/edit-editor-task/${taskId}`);
  };

  // ✅ ADDED: Delete task function
  const handleDeleteTask = async (taskId, taskName) => {
    if (!window.confirm(`Are you sure you want to delete task: "${taskName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      setDeletingTask(taskId);
      setMessage('Deleting task...');
      
      console.log('🔄 Attempting to delete task ID:', taskId);
      
      const response = await fetch(`${API_BASE}/deletetask.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          task_id: taskId
        })
      });

      const data = await response.json();
      console.log('📥 Delete response:', data);
      
      if (data.success) {
        setMessage(`✅ Task "${taskName}" deleted successfully!`);
        
        // Update local state by removing the deleted task
        setTasks(prevTasks => prevTasks.filter(task => task.id !== taskId));
        
        // Clear message after 3 seconds
        setTimeout(() => {
          setMessage('');
        }, 3000);
      } else {
        throw new Error(data.message || 'Failed to delete task');
      }
    } catch (error) {
      console.error('❌ Error deleting task:', error);
      setMessage(`❌ Error: ${error.message}`);
    } finally {
      setDeletingTask(null);
    }
  };

  const handleViewAdminNotes = (task) => {
    setSelectedTask(task);
    setAdminNotes(task.admin_notes || '');
    setShowAdminNotesModal(true);
  };

  const handleSaveAdminNotes = async () => {
    if (!selectedTask) return;

    try {
      setSavingNotes(true);
      
      const userId = sessionStorage.getItem('userId');
      const userName = sessionStorage.getItem('userName');

      const response = await fetch(`${API_BASE}/update_admin_notes.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          task_id: selectedTask.id,
          admin_notes: adminNotes.trim(),
          updated_by: userId,
          updated_by_name: userName
        })
      });

      const data = await response.json();

      if (data.success) {
        setMessage('✅ Admin notes updated successfully!');
        
        // Update task in local state
        setTasks(prevTasks => 
          prevTasks.map(task => 
            task.id === selectedTask.id 
              ? { 
                  ...task, 
                  admin_notes: adminNotes.trim() || null
                }
              : task
          )
        );
        
        setShowAdminNotesModal(false);
        setSelectedTask(null);
        setAdminNotes('');
        
        // Refresh tasks after delay
        setTimeout(() => {
          fetchEditorTasks();
        }, 1500);
      } else {
        throw new Error(data.message || 'Failed to update admin notes');
      }
    } catch (error) {
      console.error('Error updating admin notes:', error);
      setMessage(`❌ Error: ${error.message}`);
    } finally {
      setSavingNotes(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === '0000-00-00' || dateString === 'NULL') return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString || dateString === '0000-00-00' || dateString === 'NULL') return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'pending': { bg: '#F59E0B', text: 'Pending', icon: 'fa-clock' },
      'in_progress': { bg: '#06B6D4', text: 'In Progress', icon: 'fa-spinner' },
      'completed': { bg: '#10B981', text: 'Completed', icon: 'fa-check' },
      'needs_revision': { bg: '#EF4444', text: 'Needs Revision', icon: 'fa-redo' },
      'assigned': { bg: '#6366F1', text: 'Assigned', icon: 'fa-user-check' },
      'under_review': { bg: '#8B5CF6', text: 'Under Review', icon: 'fa-eye' }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    
    return (
      <span style={{
        background: config.bg,
        color: 'white',
        padding: '4px 8px',
        borderRadius: '8px',
        fontSize: '0.75rem',
        fontWeight: '600',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px'
      }}>
        <i className={`fas ${config.icon}`} style={{ fontSize: '0.6rem' }}></i>
        {config.text}
      </span>
    );
  };

  const getDesignerStatusBadge = (status) => {
    const statusConfig = {
      'pending': { bg: '#F59E0B', text: 'Pending', icon: 'fa-clock' },
      'assigned': { bg: '#6366F1', text: 'Assigned', icon: 'fa-user-check' },
      'in_progress': { bg: '#06B6D4', text: 'In Progress', icon: 'fa-spinner' },
      'completed': { bg: '#10B981', text: 'Completed', icon: 'fa-check' },
      'reviewed': { bg: '#8B5CF6', text: 'Reviewed', icon: 'fa-eye' }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    
    return (
      <span style={{
        background: config.bg,
        color: 'white',
        padding: '4px 8px',
        borderRadius: '8px',
        fontSize: '0.75rem',
        fontWeight: '600',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px'
      }}>
        <i className={`fas ${config.icon}`} style={{ fontSize: '0.6rem' }}></i>
        {config.text}
      </span>
    );
  };

 const getClientStatusBadge = (status) => {
    const statusConfig = {
        'pending': { bg: '#F59E0B', text: 'Pending', icon: 'fa-clock' },
        'reviewed': { bg: '#8B5CF6', text: 'Reviewed', icon: 'fa-eye' },
        'approved': { bg: '#10B981', text: 'Approved', icon: 'fa-check' },
        'rejected': { bg: '#EF4444', text: 'Rejected', icon: 'fa-times' },
        'feedback': { bg: '#06B6D4', text: 'Feedback', icon: 'fa-comment' },
        'proved': { bg: '#10B981', text: 'Proved', icon: 'fa-check-circle' },
        'not_proved': { bg: '#EF4444', text: 'Not Proved', icon: 'fa-times-circle' }
    };
    
    // Handle case-insensitive status
    const statusLower = status?.toLowerCase() || 'pending';
    
    const config = statusConfig[statusLower] || { bg: '#6B7280', text: 'Unknown', icon: 'fa-question' };
    
    return (
        <span style={{
            background: config.bg,
            color: 'white',
            padding: '4px 8px',
            borderRadius: '8px',
            fontSize: '0.75rem',
            fontWeight: '600',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px'
        }}>
            <i className={`fas ${config.icon}`} style={{ fontSize: '0.6rem' }}></i>
            {config.text}
        </span>
    );
};

  const getVideoTypeBadge = (videoType) => {
    const types = {
      'indoor_reel': { color: '#6366F1', label: 'Indoor Reel', icon: 'fa-home' },
      'outdoor_reel': { color: '#10B981', label: 'Outdoor Reel', icon: 'fa-tree' },
      'podcast': { color: '#8B5CF6', label: 'Podcast', icon: 'fa-podcast' },
      'indoor_long_video': { color: '#F59E0B', label: 'Indoor Long', icon: 'fa-video' },
      'outdoor_long_video': { color: '#06B6D4', label: 'Outdoor Long', icon: 'fa-mountain' }
    };
    
    const type = types[videoType] || { color: '#6B7280', label: 'Unknown', icon: 'fa-question' };
    
    return (
      <span style={{
        background: type.color,
        color: 'white',
        padding: '4px 8px',
        borderRadius: '8px',
        fontSize: '0.75rem',
        fontWeight: '600',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px'
      }}>
        <i className={`fas ${type.icon}`} style={{ fontSize: '0.6rem' }}></i>
        {type.label}
      </span>
    );
  };

  const openLink = (url) => {
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  // Filter tasks based on search term
  const filteredTasks = tasks.filter(task => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    return (
      (task.video_name && task.video_name.toLowerCase().includes(searchLower)) ||
      (task.client_name && task.client_name.toLowerCase().includes(searchLower)) ||
      (task.designer_name && task.designer_name.toLowerCase().includes(searchLower)) ||
      (task.created_by_name && task.created_by_name.toLowerCase().includes(searchLower)) ||
      (task.status && task.status.toLowerCase().includes(searchLower)) ||
      (task.video_type && task.video_type.toLowerCase().includes(searchLower)) ||
      (task.designer_notes && task.designer_notes.toLowerCase().includes(searchLower)) ||
      (task.client_status && task.client_status.toLowerCase().includes(searchLower)) ||
      (task.client_notes && task.client_notes.toLowerCase().includes(searchLower)) ||
      (task.admin_notes && task.admin_notes.toLowerCase().includes(searchLower))
    );
  });

  // Calculate statistics
  const stats = {
    total: tasks.length,
    pending: tasks.filter(t => t.status === 'pending').length,
    in_progress: tasks.filter(t => t.status === 'in_progress').length,
    completed: tasks.filter(t => t.status === 'completed').length,
    withDesigner: tasks.filter(t => t.designer_id && t.designer_id > 0).length,
    withoutDesigner: tasks.filter(t => !t.designer_id || t.designer_id == 0).length
  };

  if (loading) {
    return (
      <Layout>
        <div style={{
          minHeight: '100vh',
          background: `linear-gradient(135deg, ${colors.backgroundDark} 0%, ${colors.backgroundMedium} 50%, ${colors.backgroundLight} 100%)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{
            color: colors.textLight,
            fontSize: '1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <div style={{
              width: '20px',
              height: '20px',
              border: `2px solid ${colors.primary}`,
              borderTop: `2px solid transparent`,
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
            Loading editor tasks...
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div style={{
        minHeight: '100vh',
        background: `linear-gradient(135deg, ${colors.backgroundDark} 0%, ${colors.backgroundMedium} 50%, ${colors.backgroundLight} 100%)`,
        padding: '20px',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{
          marginBottom: '30px',
          padding: '20px',
          background: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '15px',
          border: `1px solid ${colors.borderDark}`
        }}>
          <h1 style={{
            color: 'white',
            marginBottom: '10px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <i className="fas fa-tasks"></i>
            All Editor Tasks
          </h1>
          <p style={{ color: colors.textLight }}>
            View and manage all editor tasks with designer assignments
          </p>
          
          {/* Statistics */}
          <div style={{
            display: 'flex',
            gap: '15px',
            marginTop: '20px',
            flexWrap: 'wrap'
          }}>
            <div style={{
              background: 'rgba(255, 255, 255, 0.1)',
              padding: '12px 20px',
              borderRadius: '10px',
              border: `1px solid ${colors.borderDark}`,
              minWidth: '120px'
            }}>
              <div style={{
                fontSize: '1.5rem',
                fontWeight: '700',
                color: colors.primary,
                margin: 0
              }}>
                {stats.total}
              </div>
              <div style={{
                fontSize: '0.8rem',
                color: colors.textLight,
                margin: '5px 0 0 0',
                opacity: 0.8
              }}>
                Total Tasks
              </div>
            </div>
            
            <div style={{
              background: 'rgba(16, 185, 129, 0.1)',
              padding: '12px 20px',
              borderRadius: '10px',
              border: `1px solid ${colors.success}30`,
              minWidth: '120px'
            }}>
              <div style={{
                fontSize: '1.5rem',
                fontWeight: '700',
                color: colors.success,
                margin: 0
              }}>
                {stats.completed}
              </div>
              <div style={{
                fontSize: '0.8rem',
                color: colors.textLight,
                margin: '5px 0 0 0',
                opacity: 0.8
              }}>
                Completed
              </div>
            </div>
            
            <div style={{
              background: 'rgba(6, 182, 212, 0.1)',
              padding: '12px 20px',
              borderRadius: '10px',
              border: `1px solid ${colors.accent}30`,
              minWidth: '120px'
            }}>
              <div style={{
                fontSize: '1.5rem',
                fontWeight: '700',
                color: colors.accent,
                margin: 0
              }}>
                {stats.in_progress}
              </div>
              <div style={{
                fontSize: '0.8rem',
                color: colors.textLight,
                margin: '5px 0 0 0',
                opacity: 0.8
              }}>
                In Progress
              </div>
            </div>
            
            <div style={{
              background: 'rgba(99, 102, 241, 0.1)',
              padding: '12px 20px',
              borderRadius: '10px',
              border: `1px solid ${colors.primary}30`,
              minWidth: '120px'
            }}>
              <div style={{
                fontSize: '1.5rem',
                fontWeight: '700',
                color: colors.primary,
                margin: 0
              }}>
                {stats.withDesigner}
              </div>
              <div style={{
                fontSize: '0.8rem',
                color: colors.textLight,
                margin: '5px 0 0 0',
                opacity: 0.8
              }}>
                With Designer
              </div>
            </div>
            
            <div style={{
              background: 'rgba(245, 158, 11, 0.1)',
              padding: '12px 20px',
              borderRadius: '10px',
              border: `1px solid ${colors.warning}30`,
              minWidth: '120px'
            }}>
              <div style={{
                fontSize: '1.5rem',
                fontWeight: '700',
                color: colors.warning,
                margin: 0
              }}>
                {stats.withoutDesigner}
              </div>
              <div style={{
                fontSize: '0.8rem',
                color: colors.textLight,
                margin: '5px 0 0 0',
                opacity: 0.8
              }}>
                Without Designer
              </div>
            </div>
          </div>
          
          <div style={{
            display: 'flex',
            gap: '15px',
            marginTop: '20px',
            flexWrap: 'wrap',
            alignItems: 'center'
          }}>
            <button
              onClick={fetchEditorTasks}
              disabled={loading}
              style={{
                background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                opacity: loading ? 0.7 : 1
              }}
            >
              <i className={`fas fa-sync ${loading ? 'fa-spin' : ''}`}></i>
              Refresh
            </button>
            
            {/* Search Input */}
            <div style={{ flex: 1, maxWidth: '300px' }}>
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  placeholder="Search tasks..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    width: '100%',
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: `1px solid ${colors.borderDark}`,
                    color: 'white',
                    padding: '8px 35px 8px 12px',
                    borderRadius: '8px',
                    fontSize: '0.85rem'
                  }}
                />
                <i className="fas fa-search" style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: colors.textLight,
                  fontSize: '0.85rem'
                }}></i>
              </div>
            </div>
          </div>
        </div>

        {/* Message Alert */}
        {message && (
          <div style={{
            marginBottom: '20px',
            padding: '15px',
            borderRadius: '10px',
            background: message.includes('✅') 
              ? `rgba(16, 185, 129, 0.2)` 
              : message.includes('❌')
              ? `rgba(239, 68, 68, 0.2)`
              : `rgba(59, 130, 246, 0.2)`,
            border: `1px solid ${message.includes('✅') ? colors.success : message.includes('❌') ? colors.danger : colors.primary}`,
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <i className={`fas ${message.includes('✅') ? 'fa-check-circle' : message.includes('❌') ? 'fa-exclamation-circle' : 'fa-info-circle'}`}
                 style={{ 
                   color: message.includes('✅') ? colors.success : 
                          message.includes('❌') ? colors.danger : colors.primary 
                 }}></i>
              <span>{message}</span>
            </div>
            <button
              onClick={() => setMessage('')}
              style={{
                background: 'none',
                border: 'none',
                color: colors.textLight,
                cursor: 'pointer'
              }}
            >
              <i className="fas fa-times"></i>
            </button>
          </div>
        )}

        {/* Tasks Table - Fixed height with scroll */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '15px',
          border: `1px solid ${colors.borderDark}`,
          overflow: 'hidden',
          height: 'calc(100vh - 320px)',
          display: 'flex',
          flexDirection: 'column'
        }}>
          <div style={{
            background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
            color: 'white',
            padding: '15px 20px',
            fontWeight: '600',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexShrink: 0
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <i className="fas fa-table"></i>
              Editor Tasks Table
            </div>
            <div style={{ fontSize: '0.85rem', opacity: 0.9 }}>
              Showing {filteredTasks.length} of {tasks.length} tasks
            </div>
          </div>

          <div style={{ 
            overflowX: 'auto',
            overflowY: 'auto',
            flex: 1
          }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              color: 'white',
              fontSize: '0.85rem',
              tableLayout: 'fixed',
              minWidth: '1800px'
            }}>
              <thead>
                <tr style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  borderBottom: `2px solid ${colors.borderDark}`,
                  position: 'sticky',
                  top: 0,
                  zIndex: 1
                }}>
                  <th style={{ padding: '12px 15px', textAlign: 'left', width: '60px' }}>ID</th>
                  <th style={{ padding: '12px 15px', textAlign: 'left', width: '180px' }}>Video Name</th>
                  <th style={{ padding: '12px 15px', textAlign: 'left', width: '100px' }}>Type</th>
                  <th style={{ padding: '12px 15px', textAlign: 'left', width: '120px' }}>Client</th>
                  
                  {/* New Editor Columns */}
                  <th style={{ padding: '12px 15px', textAlign: 'left', width: '120px' }}>Editor</th>
                  <th style={{ padding: '12px 15px', textAlign: 'left', width: '120px' }}>Created Date</th>
                  
                  <th style={{ padding: '12px 15px', textAlign: 'left', width: '100px' }}>Editor Status</th>
                  <th style={{ padding: '12px 15px', textAlign: 'left', width: '120px' }}>Designer</th>
                  <th style={{ padding: '12px 15px', textAlign: 'left', width: '100px' }}>Designer Status</th>
                  <th style={{ padding: '12px 15px', textAlign: 'left', width: '100px' }}>Thumbnail</th>
                  <th style={{ padding: '12px 15px', textAlign: 'left', width: '150px' }}>Designer Notes</th>
                  <th style={{ padding: '12px 15px', textAlign: 'left', width: '100px' }}>Client Status</th>
                  <th style={{ padding: '12px 15px', textAlign: 'left', width: '150px' }}>Client Notes</th>
                  <th style={{ padding: '12px 15px', textAlign: 'left', width: '80px' }}>Video Count</th>
                  <th style={{ padding: '12px 15px', textAlign: 'left', width: '150px' }}>Admin Notes</th>
                  <th style={{ padding: '12px 15px', textAlign: 'left', width: '100px' }}>Raw Link</th>
                  <th style={{ padding: '12px 15px', textAlign: 'left', width: '150px' }}>Task Notes</th>
                  <th style={{ padding: '12px 15px', textAlign: 'left', width: '120px' }}>Actions</th> {/* Increased width */}
                </tr>
              </thead>
              <tbody>
                {filteredTasks.length === 0 ? (
                  <tr>
                    <td colSpan="18" style={{ padding: '40px', textAlign: 'center', color: colors.textLight }}>
                      <i className="fas fa-search fa-2x" style={{ marginBottom: '15px', opacity: 0.5 }}></i>
                      <div>No tasks found</div>
                      {searchTerm && (
                        <div style={{ marginTop: '10px', fontSize: '0.9rem' }}>
                          No tasks match your search: "<strong>{searchTerm}</strong>"
                        </div>
                      )}
                    </td>
                  </tr>
                ) : (
                  filteredTasks.map((task) => (
                    <tr key={task.id} style={{
                      borderBottom: `1px solid ${colors.borderDark}`,
                      transition: 'background 0.3s'
                    }}>
                      <td style={{ padding: '12px 15px', fontWeight: '600' }}>
                        #{task.id}
                      </td>
                      <td style={{ padding: '12px 15px', fontWeight: '500' }}>
                        {task.video_name}
                      </td>
                      <td style={{ padding: '12px 15px' }}>
                        {getVideoTypeBadge(task.video_type)}
                      </td>
                      <td style={{ padding: '12px 15px' }}>
                        {task.client_name || 'N/A'}
                      </td>
                      
                      {/* Created By Column */}
                      <td style={{ padding: '12px 15px' }}>
                        {task.created_by_name ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <i className="fas fa-user-plus" style={{ color: colors.accent, fontSize: '0.8rem' }}></i>
                            <span style={{ fontWeight: '500', color: colors.textLight }}>
                              {task.created_by_name}
                            </span>
                          </div>
                        ) : (
                          <span style={{ color: colors.textLight, fontSize: '0.8rem' }}>
                            System
                          </span>
                        )}
                      </td>
                      
                      {/* Created Date Column */}
                      <td style={{ padding: '12px 15px' }}>
                        <div style={{ 
                          fontSize: '0.8rem',
                          color: colors.textLight 
                        }}>
                          {formatDateTime(task.created_at)}
                        </div>
                      </td>
                      
                      <td style={{ padding: '12px 15px' }}>
                        {getStatusBadge(task.status)}
                      </td>
                      <td style={{ padding: '12px 15px' }}>
                        {task.designer_name && task.designer_name !== 'Not Assigned' ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <i className="fas fa-user-check" style={{ color: colors.success, fontSize: '0.8rem' }}></i>
                            <span style={{ color: colors.success, fontWeight: '500' }}>
                              {task.designer_name}
                            </span>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <i className="fas fa-user-times" style={{ color: colors.warning, fontSize: '0.8rem' }}></i>
                            <span style={{ color: colors.warning, fontWeight: '500' }}>
                              Unassigned
                            </span>
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '12px 15px' }}>
                        {task.designer_status ? getDesignerStatusBadge(task.designer_status) : (
                          <span style={{ color: colors.textLight, fontSize: '0.8rem' }}>
                            N/A
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '12px 15px' }}>
                        {task.thumbnail_link ? (
                          <button
                            onClick={() => openLink(task.thumbnail_link)}
                            style={{
                              background: `linear-gradient(135deg, ${colors.accent}, #22D3EE)`,
                              color: 'white',
                              border: 'none',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '0.7rem',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              maxWidth: '100%'
                            }}
                            title={task.thumbnail_link}
                          >
                            <i className="fas fa-external-link-alt"></i>
                            Thumbnail
                          </button>
                        ) : (
                          <span style={{ 
                            color: colors.textLight, 
                            fontSize: '0.8rem',
                            fontStyle: 'italic'
                          }}>
                            No thumbnail
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '12px 15px' }}>
                        <div style={{
                          maxHeight: '60px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          lineHeight: '1.2',
                          color: colors.textLight,
                          fontSize: '0.8rem'
                        }}>
                          {task.designer_notes || 'No notes'}
                        </div>
                      </td>
                      <td style={{ padding: '12px 15px' }}>
                        {task.client_status ? getClientStatusBadge(task.client_status) : (
                          <span style={{ color: colors.textLight, fontSize: '0.8rem' }}>
                            N/A
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '12px 15px' }}>
                        <div style={{
                          maxHeight: '60px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          lineHeight: '1.2',
                          color: colors.textLight,
                          fontSize: '0.8rem'
                        }}>
                          {task.client_notes || 'No notes'}
                        </div>
                      </td>
                      <td style={{ padding: '12px 15px', textAlign: 'center', fontWeight: '600' }}>
                        {task.video_count || 1}
                      </td>
                      <td style={{ padding: '12px 15px' }}>
                        <div style={{
                          maxHeight: '60px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          lineHeight: '1.2',
                          color: colors.textLight,
                          fontSize: '0.8rem'
                        }}>
                          {task.admin_notes || 'No admin notes'}
                        </div>
                        <button
                          onClick={() => handleViewAdminNotes(task)}
                          style={{
                            background: `rgba(255, 255, 255, 0.1)`,
                            color: colors.textLight,
                            border: `1px solid ${colors.borderDark}`,
                            padding: '4px 8px',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '0.7rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            width: '100%',
                            justifyContent: 'center',
                            marginTop: '4px'
                          }}
                          title="View/Edit Admin Notes"
                        >
                          <i className="fas fa-edit"></i>
                          Edit
                        </button>
                      </td>
                      <td style={{ padding: '12px 15px' }}>
                        {task.raw_link ? (
                          <button
                            onClick={() => openLink(task.raw_link)}
                            style={{
                              background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
                              color: 'white',
                              border: 'none',
                              padding: '6px 12px',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '0.75rem',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '5px'
                            }}
                            title={task.raw_link}
                          >
                            <i className="fas fa-external-link-alt"></i>
                            View
                          </button>
                        ) : (
                          <span style={{ color: colors.textLight, fontSize: '0.8rem' }}>N/A</span>
                        )}
                      </td>
                      <td style={{ padding: '12px 15px' }}>
                        {task.notes ? (
                          <div style={{
                            color: colors.textLight,
                            fontSize: '0.8rem',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: '100%',
                            cursor: 'help'
                          }} title={task.notes}>
                            {task.notes.length > 20 ? task.notes.substring(0, 20) + '...' : task.notes}
                          </div>
                        ) : (
                          <span style={{ color: colors.textLight, fontSize: '0.8rem', fontStyle: 'italic' }}>
                            No notes
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '12px 15px' }}>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          <button
                            onClick={() => handleEditClick(task.id)}
                            style={{
                              background: `linear-gradient(135deg, ${colors.warning}, #eab308)`,
                              color: 'white',
                              border: 'none',
                              padding: '6px 12px',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '0.75rem',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '5px',
                              flex: 1,
                              minWidth: '80px'
                            }}
                            title="Edit Task"
                          >
                            <i className="fas fa-edit"></i>
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteTask(task.id, task.video_name)}
                            disabled={deletingTask === task.id}
                            style={{
                              background: `linear-gradient(135deg, ${colors.danger}, #dc2626)`,
                              color: 'white',
                              border: 'none',
                              padding: '6px 12px',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '0.75rem',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '5px',
                              flex: 1,
                              minWidth: '80px',
                              opacity: deletingTask === task.id ? 0.7 : 1
                            }}
                            title="Delete Task"
                          >
                            {deletingTask === task.id ? (
                              <>
                                <div style={{
                                  width: '12px',
                                  height: '12px',
                                  border: '2px solid white',
                                  borderTop: '2px solid transparent',
                                  borderRadius: '50%',
                                  animation: 'spin 1s linear infinite'
                                }}></div>
                                Deleting...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-trash"></i>
                                Delete
                              </>
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Admin Notes Modal */}
      {showAdminNotesModal && selectedTask && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: `linear-gradient(135deg, ${colors.backgroundDark} 0%, ${colors.backgroundMedium} 100%)`,
            borderRadius: '15px',
            border: `1px solid ${colors.borderDark}`,
            width: '100%',
            maxWidth: '500px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            {/* Modal Header */}
            <div style={{
              background: `linear-gradient(135deg, ${colors.info}, #3B82F6)`,
              color: 'white',
              padding: '20px',
              borderRadius: '15px 15px 0 0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
                <i className="fas fa-sticky-note"></i>
                Admin Notes - Task #{selectedTask.id}
              </h3>
              <button
                onClick={() => {
                  setShowAdminNotesModal(false);
                  setSelectedTask(null);
                  setAdminNotes('');
                }}
                style={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  border: 'none',
                  color: 'white',
                  borderRadius: '50%',
                  width: '32px',
                  height: '32px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '20px' }}>
              {/* Task Info */}
              <div style={{
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '10px',
                padding: '15px',
                marginBottom: '20px',
                border: `1px solid ${colors.borderDark}`
              }}>
                <div style={{ marginBottom: '10px' }}>
                  <span style={{ 
                    color: colors.textLight, 
                    fontSize: '0.85rem',
                    display: 'block'
                  }}>
                    Task #{selectedTask.id}
                  </span>
                  <span style={{ 
                    color: 'white', 
                    fontWeight: '500',
                    fontSize: '1.1rem'
                  }}>
                    {selectedTask.video_name}
                  </span>
                </div>
                <div style={{ 
                  color: colors.textLight, 
                  fontSize: '0.85rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginTop: '5px'
                }}>
                  <i className="fas fa-user"></i>
                  Created by: {selectedTask.created_by_name || 'System'}
                </div>
                <div style={{ 
                  color: colors.textLight, 
                  fontSize: '0.85rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginTop: '5px'
                }}>
                  <i className="fas fa-user-tie"></i>
                  Client: {selectedTask.client_name || 'No client'}
                </div>
              </div>

              {/* Admin Notes Display/Edit */}
              <div style={{
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '10px',
                padding: '15px',
                marginBottom: '20px',
                border: `1px solid ${colors.borderDark}`
              }}>
                <label style={{ 
                  color: colors.textLight, 
                  display: 'block', 
                  marginBottom: '10px',
                  fontSize: '0.9rem',
                  fontWeight: '500'
                }}>
                  <i className="fas fa-sticky-note" style={{ marginRight: '8px' }}></i>
                  Admin Notes
                </label>
                <textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Enter admin notes..."
                  rows="5"
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: 'rgba(255, 255, 255, 0.07)',
                    border: `2px solid ${colors.borderDark}`,
                    borderRadius: '8px',
                    color: 'white',
                    fontSize: '0.9rem',
                    resize: 'vertical',
                    minHeight: '120px'
                  }}
                />
                <div style={{ 
                  fontSize: '0.8rem', 
                  color: colors.textLight, 
                  marginTop: '8px',
                  opacity: 0.7
                }}>
                  <i className="fas fa-info-circle"></i> 
                  You can update admin notes anytime. They will be saved independently of approval status.
                </div>
              </div>

              {/* Action Buttons */}
              <div style={{
                display: 'flex',
                gap: '10px',
                marginTop: '20px'
              }}>
                <button
                  onClick={handleSaveAdminNotes}
                  disabled={savingNotes}
                  style={{
                    background: `linear-gradient(135deg, ${colors.success}, #059669)`,
                    color: 'white',
                    border: 'none',
                    padding: '12px 20px',
                    borderRadius: '8px',
                    cursor: savingNotes ? 'not-allowed' : 'pointer',
                    opacity: savingNotes ? 0.7 : 1,
                    fontSize: '0.9rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    flex: 2,
                    justifyContent: 'center',
                    fontWeight: '600'
                  }}
                >
                  {savingNotes ? (
                    <>
                      <div style={{
                        width: '16px',
                        height: '16px',
                        border: `2px solid white`,
                        borderTop: `2px solid transparent`,
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }}></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save"></i>
                      Save Notes
                    </>
                  )}
                </button>
                
                <button
                  onClick={() => {
                    setShowAdminNotesModal(false);
                    setSelectedTask(null);
                    setAdminNotes('');
                  }}
                  style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    color: colors.textLight,
                    border: `1px solid ${colors.borderDark}`,
                    padding: '12px 20px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    flex: 1
                  }}
                >
                  <i className="fas fa-times"></i>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        tr:hover {
          background: rgba(255, 255, 255, 0.03) !important;
        }
        
        select:focus {
          outline: none;
          border-color: ${colors.primary} !important;
          box-shadow: 0 0 0 2px ${colors.primary}20;
        }
        
        select option {
          background: ${colors.backgroundDark};
          color: white;
          padding: 8px;
        }
        
        select option:checked {
          background: ${colors.primary};
          color: white;
        }
        
        input:focus {
          outline: none;
          border-color: ${colors.primary} !important;
          box-shadow: 0 0 0 2px ${colors.primary}20;
        }
        
        textarea:focus {
          outline: none;
          border-color: ${colors.primary} !important;
          box-shadow: 0 0 0 2px ${colors.primary}20;
        }
        
        /* Custom scrollbar for table */
        div::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        
        div::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.05);
          border-radius: 4px;
        }
        
        div::-webkit-scrollbar-thumb {
          background: ${colors.primary};
          border-radius: 4px;
        }
        
        div::-webkit-scrollbar-thumb:hover {
          background: ${colors.secondary};
        }
      `}</style>
    </Layout>
  );
}

export default AllEditorTasks;